import React, { createContext, useContext, useReducer, useEffect, useMemo, ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { PerformanceZonesService } from '@/services/api/performance-zones.service';
import { PerformanceZonesCacheService } from '@/services/api/performance-zones-cache.service';
import { usePerformanceZonesRealtime } from '@/hooks/useOptimizedRealtimeUpdates';
import type { 
  PerformanceZonesSummary, 
  CourseDiscoveryItem,
  HoleRecord,
  CourseRecord,
  LocalLegendStatus,
  PerformanceRanking
} from '@/types/performance-zones';

// Action Types
export enum PerformanceZonesActionType {
  SET_LOADING = 'SET_LOADING',
  SET_ERROR = 'SET_ERROR',
  SET_PERFORMANCE_DATA = 'SET_PERFORMANCE_DATA',
  SET_COURSE_DISCOVERY = 'SET_COURSE_DISCOVERY',
  SET_STARRED_COURSES = 'SET_STARRED_COURSES',
  UPDATE_COURSE = 'UPDATE_COURSE',
  TOGGLE_STAR_COURSE = 'TOGGLE_STAR_COURSE',
  UPDATE_PERFORMANCE_STATS = 'UPDATE_PERFORMANCE_STATS',
  ADD_RECORD = 'ADD_RECORD',
  UPDATE_LOCAL_LEGEND = 'UPDATE_LOCAL_LEGEND',
  SET_ACTIVE_TAB = 'SET_ACTIVE_TAB',
  SET_SEARCH_QUERY = 'SET_SEARCH_QUERY',
  SET_VIEW_MODE = 'SET_VIEW_MODE',
  CLEAR_ERROR = 'CLEAR_ERROR',
  REFRESH_DATA = 'REFRESH_DATA'
}

// State Interface
export interface PerformanceZonesState {
  // Data
  performanceData: PerformanceZonesSummary | null;
  courseDiscovery: CourseDiscoveryItem[];
  starredCourses: string[]; // Course IDs
  
  // UI State
  activeTab: 'records' | 'legends' | 'top10' | 'courses';
  searchQuery: string;
  viewMode: 'favorites' | 'all';
  
  // Loading & Error States
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  
  // Filters & Computed Data
  filteredCourses: CourseDiscoveryItem[];
  totalRecords: number;
  totalLegends: number;
  bestRanking: number | null;
  
  // Cache metadata
  lastUpdated: number;
  dataVersion: number;
}

// Action Interface
export interface PerformanceZonesAction {
  type: PerformanceZonesActionType;
  payload?: any;
}

// Initial State
const initialState: PerformanceZonesState = {
  performanceData: null,
  courseDiscovery: [],
  starredCourses: [],
  activeTab: 'records',
  searchQuery: '',
  viewMode: 'favorites',
  isLoading: true,
  isRefreshing: false,
  error: null,
  filteredCourses: [],
  totalRecords: 0,
  totalLegends: 0,
  bestRanking: null,
  lastUpdated: 0,
  dataVersion: 1
};

// Reducer Function
function performanceZonesReducer(
  state: PerformanceZonesState, 
  action: PerformanceZonesAction
): PerformanceZonesState {
  switch (action.type) {
    case PerformanceZonesActionType.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
        error: action.payload ? null : state.error
      };

    case PerformanceZonesActionType.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
        isRefreshing: false
      };

    case PerformanceZonesActionType.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };

    case PerformanceZonesActionType.SET_PERFORMANCE_DATA:
      const performanceData = action.payload;
      return {
        ...state,
        performanceData,
        totalRecords: (performanceData?.hole_records?.length || 0) + (performanceData?.course_records?.length || 0),
        totalLegends: performanceData?.local_legend_courses?.length || 0,
        bestRanking: performanceData?.stats?.best_ranking_position || null,
        lastUpdated: Date.now(),
        dataVersion: state.dataVersion + 1,
        isLoading: false,
        error: null
      };

    case PerformanceZonesActionType.SET_COURSE_DISCOVERY:
      return {
        ...state,
        courseDiscovery: action.payload,
        filteredCourses: filterCourses(action.payload, state.searchQuery, state.viewMode, state.starredCourses)
      };

    case PerformanceZonesActionType.SET_STARRED_COURSES:
      return {
        ...state,
        starredCourses: action.payload,
        filteredCourses: filterCourses(state.courseDiscovery, state.searchQuery, state.viewMode, action.payload)
      };

    case PerformanceZonesActionType.TOGGLE_STAR_COURSE:
      const courseId = action.payload;
      const newStarredCourses = state.starredCourses.includes(courseId)
        ? state.starredCourses.filter(id => id !== courseId)
        : [...state.starredCourses, courseId];
      
      return {
        ...state,
        starredCourses: newStarredCourses,
        filteredCourses: filterCourses(state.courseDiscovery, state.searchQuery, state.viewMode, newStarredCourses)
      };

    case PerformanceZonesActionType.UPDATE_COURSE:
      const updatedCourse = action.payload;
      const updatedDiscovery = state.courseDiscovery.map(course =>
        course.id === updatedCourse.id ? { ...course, ...updatedCourse } : course
      );
      
      return {
        ...state,
        courseDiscovery: updatedDiscovery,
        filteredCourses: filterCourses(updatedDiscovery, state.searchQuery, state.viewMode, state.starredCourses)
      };

    case PerformanceZonesActionType.SET_ACTIVE_TAB:
      return {
        ...state,
        activeTab: action.payload
      };

    case PerformanceZonesActionType.SET_SEARCH_QUERY:
      return {
        ...state,
        searchQuery: action.payload,
        filteredCourses: filterCourses(state.courseDiscovery, action.payload, state.viewMode, state.starredCourses)
      };

    case PerformanceZonesActionType.SET_VIEW_MODE:
      return {
        ...state,
        viewMode: action.payload,
        filteredCourses: filterCourses(state.courseDiscovery, state.searchQuery, action.payload, state.starredCourses)
      };

    case PerformanceZonesActionType.REFRESH_DATA:
      return {
        ...state,
        isRefreshing: action.payload,
        error: action.payload ? null : state.error
      };

    case PerformanceZonesActionType.ADD_RECORD:
      if (!state.performanceData) return state;
      
      const newRecord = action.payload;
      const updatedRecords = newRecord.type === 'hole' 
        ? [...(state.performanceData.hole_records || []), newRecord]
        : [...(state.performanceData.course_records || []), newRecord];
      
      return {
        ...state,
        performanceData: {
          ...state.performanceData,
          hole_records: newRecord.type === 'hole' ? updatedRecords : state.performanceData.hole_records,
          course_records: newRecord.type === 'course' ? updatedRecords : state.performanceData.course_records
        },
        totalRecords: state.totalRecords + 1,
        dataVersion: state.dataVersion + 1
      };

    case PerformanceZonesActionType.UPDATE_LOCAL_LEGEND:
      if (!state.performanceData) return state;
      
      const legendUpdate = action.payload;
      const updatedLegends = state.performanceData.local_legend_courses?.map(legend =>
        legend.course_id === legendUpdate.course_id ? { ...legend, ...legendUpdate } : legend
      ) || [];
      
      return {
        ...state,
        performanceData: {
          ...state.performanceData,
          local_legend_courses: updatedLegends
        },
        dataVersion: state.dataVersion + 1
      };

    default:
      return state;
  }
}

// Helper Functions
function filterCourses(
  courses: CourseDiscoveryItem[], 
  searchQuery: string, 
  viewMode: 'favorites' | 'all',
  starredCourses: string[]
): CourseDiscoveryItem[] {
  let filtered = courses;

  // Filter by view mode
  if (viewMode === 'favorites') {
    filtered = filtered.filter(course => starredCourses.includes(course.id));
  }

  // Filter by search query
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase();
    filtered = filtered.filter(course =>
      course.name.toLowerCase().includes(query) ||
      (course.club_name && course.club_name.toLowerCase().includes(query)) ||
      (course.location && course.location.toLowerCase().includes(query)) ||
      (course.city && course.city.toLowerCase().includes(query))
    );
  }

  return filtered;
}

// Context Interface
export interface PerformanceZonesContextValue {
  state: PerformanceZonesState;
  dispatch: React.Dispatch<PerformanceZonesAction>;
  actions: {
    setActiveTab: (tab: 'records' | 'legends' | 'top10' | 'courses') => void;
    setSearchQuery: (query: string) => void;
    setViewMode: (mode: 'favorites' | 'all') => void;
    toggleStarCourse: (courseId: string) => Promise<void>;
    refreshData: () => Promise<void>;
    clearError: () => void;
  };
  selectors: {
    getVisibleRecords: () => (HoleRecord | CourseRecord)[];
    getLocalLegends: () => LocalLegendStatus[];
    getTop10Rankings: () => { holes: PerformanceRanking[]; courses: PerformanceRanking[] };
    getFilteredCourses: () => CourseDiscoveryItem[];
    isStarred: (courseId: string) => boolean;
    getStats: () => any;
  };
}

// Context Creation
const PerformanceZonesContext = createContext<PerformanceZonesContextValue | undefined>(undefined);

// Provider Component
export interface PerformanceZonesProviderProps {
  children: ReactNode;
}

export function PerformanceZonesProvider({ children }: PerformanceZonesProviderProps) {
  const { user } = useAuth();
  const [state, dispatch] = useReducer(performanceZonesReducer, initialState);
  const performanceZonesService = useMemo(() => new PerformanceZonesService(), []);
  const cacheService = useMemo(() => new PerformanceZonesCacheService(), []);

  // Real-time updates - DISABLED on mobile to prevent WebSocket leak
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const realtimeUpdates = usePerformanceZonesRealtime(isMobile ? '' : (user?.id || ''));

  // DISABLED (Hotfix 2025-11-04): Auto-loading on every page disabled
  // Problem: Loads achievement data (30+ courses × get_local_legend RPC) on EVERY page = 200+ requests
  // Solution: Only load when user visits /performance-zones or /course-records pages
  // Manual loading via actions.refreshData() still available
  //
  // Expected Impact: 441 → ~30 requests on dashboard
  // To re-enable: Uncomment the useEffect below
  /*
  useEffect(() => {
    if (user?.id) {
      loadInitialData();
    }
  }, [user?.id]);
  */

  // Cache cleanup effect - runs every 5 minutes
  useEffect(() => {
    let cleanupInterval: NodeJS.Timeout;
    
    if (user?.id) {
      const runCleanup = () => {
        try {
          const result = cacheService.cleanupExpiredCache();
          if (result.removedCount > 0) {
            console.log(`🧹 [PERF-ZONES] Cleaned up ${result.removedCount} expired cache entries`);
          }
        } catch (error) {
          console.error('❌ [PERF-ZONES] Cache cleanup error:', error);
        }
      };

      // Run cleanup every 5 minutes
      cleanupInterval = setInterval(runCleanup, 5 * 60 * 1000);
      
      // Run cleanup once on mount
      runCleanup();
    }

    return () => {
      if (cleanupInterval) {
        clearInterval(cleanupInterval);
      }
    };
  }, [user?.id, cacheService]);

  // Handle real-time updates with cache invalidation
  useEffect(() => {
    if (realtimeUpdates.records.updates.length > 0 && user?.id) {
      console.log('📡 [PERF-ZONES] Real-time performance updates received');
      
      // Apply real-time performance updates
      realtimeUpdates.records.updates.forEach(update => {
        if (update.eventType === 'UPDATE' && update.new) {
          dispatch({
            type: PerformanceZonesActionType.UPDATE_PERFORMANCE_STATS,
            payload: update.new
          });
        }
      });

      // Invalidate cache since performance data changed
      cacheService.invalidateUserCache(user.id);
    }
  }, [realtimeUpdates.records.updates, user?.id, cacheService]);

  // Handle course updates with cache invalidation
  useEffect(() => {
    if (realtimeUpdates.courses.updates.length > 0 && user?.id) {
      console.log('📡 [PERF-ZONES] Real-time course updates received');
      
      realtimeUpdates.courses.updates.forEach(update => {
        if (update.eventType === 'UPDATE' && update.new) {
          dispatch({
            type: PerformanceZonesActionType.UPDATE_COURSE,
            payload: update.new
          });
        }
      });

      // Invalidate cache since course data changed
      cacheService.invalidateUserCache(user.id);
    }
  }, [realtimeUpdates.courses.updates, user?.id, cacheService]);

  // Actions
  const actions = useMemo(() => ({
    setActiveTab: (tab: 'records' | 'legends' | 'top10' | 'courses') => {
      dispatch({ type: PerformanceZonesActionType.SET_ACTIVE_TAB, payload: tab });
    },

    setSearchQuery: (query: string) => {
      dispatch({ type: PerformanceZonesActionType.SET_SEARCH_QUERY, payload: query });
    },

    setViewMode: (mode: 'favorites' | 'all') => {
      dispatch({ type: PerformanceZonesActionType.SET_VIEW_MODE, payload: mode });
    },

    toggleStarCourse: async (courseId: string) => {
      if (!user?.id) return;

      try {
        console.log('⭐ [PERF-ZONES] Toggling star for course:', courseId);
        
        // Optimistic update
        dispatch({ type: PerformanceZonesActionType.TOGGLE_STAR_COURSE, payload: courseId });

        // API call
        const result = await performanceZonesService.toggleStarredCourse(user.id, courseId);
        
        if (result.success) {
          // Invalidate cache since starred courses changed
          cacheService.invalidateUserCache(user.id);
          console.log('✅ [PERF-ZONES] Star toggled and cache invalidated');
        } else {
          // Revert optimistic update on failure
          dispatch({ type: PerformanceZonesActionType.TOGGLE_STAR_COURSE, payload: courseId });
          dispatch({ type: PerformanceZonesActionType.SET_ERROR, payload: 'Failed to update starred course' });
        }
      } catch (error) {
        console.error('❌ [PERF-ZONES] Error toggling star:', error);
        // Revert optimistic update on error
        dispatch({ type: PerformanceZonesActionType.TOGGLE_STAR_COURSE, payload: courseId });
        dispatch({ type: PerformanceZonesActionType.SET_ERROR, payload: 'Network error occurred' });
      }
    },

    refreshData: async () => {
      if (!user?.id) return;

      dispatch({ type: PerformanceZonesActionType.REFRESH_DATA, payload: true });
      
      try {
        console.log('🔄 [PERF-ZONES] Manual refresh initiated');
        
        // Force fresh data fetch
        const result = await performanceZonesService.getInitialPerformanceZonesData(user.id);
        
        if (result.success && result.data) {
          const freshData = {
            performanceData: result.data.performanceData,
            courseDiscovery: result.data.courseDiscovery,
            starredCourses: result.data.starredCourses,
            lastUpdated: Date.now(),
            version: state.dataVersion + 1
          };

          // Update state
          dispatch({ type: PerformanceZonesActionType.SET_PERFORMANCE_DATA, payload: result.data.performanceData });
          dispatch({ type: PerformanceZonesActionType.SET_COURSE_DISCOVERY, payload: result.data.courseDiscovery });
          dispatch({ type: PerformanceZonesActionType.SET_STARRED_COURSES, payload: result.data.starredCourses });
          
          // Update cache with fresh data
          cacheService.setCachedData(user.id, freshData, {
            ttlMinutes: 30
          });
          
          console.log('✅ [PERF-ZONES] Manual refresh completed and cached');
        } else {
          dispatch({ type: PerformanceZonesActionType.SET_ERROR, payload: result.error || 'Failed to refresh data' });
        }
      } catch (error) {
        console.error('❌ [PERF-ZONES] Error during manual refresh:', error);
        dispatch({ type: PerformanceZonesActionType.SET_ERROR, payload: 'Network error occurred' });
      } finally {
        dispatch({ type: PerformanceZonesActionType.REFRESH_DATA, payload: false });
      }
    },

    clearError: () => {
      dispatch({ type: PerformanceZonesActionType.CLEAR_ERROR });
    }
  }), [user?.id, performanceZonesService]);

  // Selectors
  const selectors = useMemo(() => ({
    getVisibleRecords: (): (HoleRecord | CourseRecord)[] => {
      if (!state.performanceData) return [];
      return [...(state.performanceData.hole_records || []), ...(state.performanceData.course_records || [])];
    },

    getLocalLegends: (): LocalLegendStatus[] => {
      return state.performanceData?.local_legend_courses || [];
    },

    getTop10Rankings: (): { holes: PerformanceRanking[]; courses: PerformanceRanking[] } => {
      return {
        holes: state.performanceData?.top_10_holes || [],
        courses: state.performanceData?.top_10_courses || []
      };
    },

    getFilteredCourses: (): CourseDiscoveryItem[] => {
      return state.filteredCourses;
    },

    isStarred: (courseId: string): boolean => {
      return state.starredCourses.includes(courseId);
    },

    getStats: () => {
      return state.performanceData?.stats || null;
    }
  }), [state]);

  // Load initial data function with optimized caching
  const loadInitialData = async () => {
    if (!user?.id) return;

    dispatch({ type: PerformanceZonesActionType.SET_LOADING, payload: true });

    try {
      console.log('🚀 [PERF-ZONES] Loading data with cache optimization...');
      
      // 1. Try to get cached data first
      const cachedData = cacheService.getCachedData(user.id, {
        maxAge: 5 * 60 * 1000 // 5 minutes max age for UI responsiveness
      });

      if (cachedData) {
        console.log('⚡ [PERF-ZONES] Loading from cache');
        
        // Load from cache immediately for fast UI
        dispatch({ type: PerformanceZonesActionType.SET_PERFORMANCE_DATA, payload: cachedData.performanceData });
        dispatch({ type: PerformanceZonesActionType.SET_COURSE_DISCOVERY, payload: cachedData.courseDiscovery });
        dispatch({ type: PerformanceZonesActionType.SET_STARRED_COURSES, payload: cachedData.starredCourses });

        // Check if we should refresh in background
        const shouldRefresh = cacheService.shouldRefreshCache(user.id);
        if (shouldRefresh.shouldRefresh) {
          console.log('🔄 [PERF-ZONES] Background refresh needed:', shouldRefresh.reason);
          // Refresh in background without showing loading state
          loadFreshDataInBackground(user.id);
        }
        
        return;
      }

      console.log('📥 [PERF-ZONES] Cache miss, loading fresh data');
      
      // 2. No cache available, load fresh data
      await loadFreshData(user.id);
      
    } catch (error) {
      console.error('❌ [PERF-ZONES] Error loading initial data:', error);
      dispatch({ type: PerformanceZonesActionType.SET_ERROR, payload: 'Network error occurred' });
    }
  };

  // Load fresh data and cache it
  const loadFreshData = async (userId: string, isBackground = false) => {
    try {
      // DEBUG: Force cache invalidation to test fix
      console.log('🗑️ [DEBUG] Invalidating cache to test round count fix...');
      cacheService.invalidateUserCache(userId);
      
      const result = await performanceZonesService.getInitialPerformanceZonesData(userId);
      
      if (result.success && result.data) {
        const freshData = {
          performanceData: result.data.performanceData,
          courseDiscovery: result.data.courseDiscovery,
          starredCourses: result.data.starredCourses,
          lastUpdated: Date.now(),
          version: state.dataVersion + 1
        };

        // Update state
        dispatch({ type: PerformanceZonesActionType.SET_PERFORMANCE_DATA, payload: result.data.performanceData });
        dispatch({ type: PerformanceZonesActionType.SET_COURSE_DISCOVERY, payload: result.data.courseDiscovery });
        dispatch({ type: PerformanceZonesActionType.SET_STARRED_COURSES, payload: result.data.starredCourses });
        
        // Cache the fresh data
        cacheService.setCachedData(userId, freshData, {
          ttlMinutes: 30 // Cache for 30 minutes
        });
        
        console.log('✅ [PERF-ZONES] Fresh data loaded and cached');
      } else {
        if (!isBackground) {
          dispatch({ type: PerformanceZonesActionType.SET_ERROR, payload: result.error || 'Failed to load data' });
        }
      }
    } catch (error) {
      console.error('❌ [PERF-ZONES] Error loading fresh data:', error);
      if (!isBackground) {
        dispatch({ type: PerformanceZonesActionType.SET_ERROR, payload: 'Network error occurred' });
      }
    }
  };

  // Background refresh without affecting UI loading state
  const loadFreshDataInBackground = async (userId: string) => {
    console.log('🔄 [PERF-ZONES] Starting background refresh...');
    await loadFreshData(userId, true);
  };

  const contextValue: PerformanceZonesContextValue = useMemo(() => ({
    state,
    dispatch,
    actions,
    selectors
  }), [state, actions, selectors]);

  return (
    <PerformanceZonesContext.Provider value={contextValue}>
      {children}
    </PerformanceZonesContext.Provider>
  );
}

// Hook to use the context
export function usePerformanceZones(): PerformanceZonesContextValue {
  const context = useContext(PerformanceZonesContext);
  if (context === undefined) {
    throw new Error('usePerformanceZones must be used within a PerformanceZonesProvider');
  }
  return context;
}